import React, { useState, useEffect } from "react"
import { Grid, Box, Tab, styled, InputAdornment, TextField, Alert } from "@mui/material"
import { TabContext, TabList } from "@mui/lab"
import { useTheme } from "@mui/system"
import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import AppButtonGradient from "components/StyledComponents/AppButtonGradient"
import AppSelect from "components/StyledComponents/AppSelect"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import { useDispatch, useSelector } from "react-redux"
import {
  getCMSRestaurantListAction,
  getCMSRestaurantGroupListAction,
  reorderCMSRestaurantsAction
} from "redux/superAdmin/actions"
import { appActions } from "redux/app/slice/app"
import { useDebounce } from "hooks/utils"
import AddIcon from "@mui/icons-material/Add"
import DragIndicatorIcon from "@mui/icons-material/DragIndicator"
import RestaurantIcon from "@mui/icons-material/Restaurant"
import GroupsIcon from "@mui/icons-material/Groups"
import RestaurantListTable from "./restaurantListTable"
import GroupListTable from "./groupListTable"
import SearchIcon from "assets/icons/Search.svg"
import RestaurantFormModal from "./RestaurantForm"
import RestaurantGroupFormModal from "./RestaurantGroupForm"

const CustomSearchField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#000",
    borderRadius: 8,
    height: 48
  }
}))

const CustomTabList = styled(TabList)(({ theme }) => ({
  "& .MuiTabs-indicator": {
    backgroundColor: theme.palette.aqua.main,
    height: "4px"
  },
  "& .MuiTab-root.Mui-selected": {
    color: theme.palette.aqua.main
  },
  "& .MuiTab-root": {
    textTransform: "none",
    padding: "20px 28px",
    ...theme.typography.body1Regular
  },
  "& .MuiTabs-scroller": {
    marginLeft: "12px"
  }
}))

const RestaurantManagement = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const [breadCrumbs, setBreadCrumbs] = useState([])
  const [activeTab, setActiveTab] = useState("restaurants")
  const [searchValue, setSearchValue] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [liteFilter, setLiteFilter] = useState(false)
  const [ownerFilter, setOwnerFilter] = useState("")
  const [modal, setModal] = useState(null)
  const [localRestaurants, setLocalRestaurants] = useState([])
  const [groupCurrentPage, setGroupCurrentPage] = useState(1)
  const [isReorderMode, setIsReorderMode] = useState(false)
  const [reorderList, setReorderList] = useState([])
  const debouncedSearch = useDebounce(searchValue, 350)

  const { cmsRestaurants, cmsRestaurantGroups } = useSelector((store) => store?.superAdmin ?? {})
  const { isLoading } = useSelector((store) => store?.app ?? {})

  const groupFilterOptions = [
    { label: "All Groups", value: "" },
    ...(cmsRestaurantGroups?.results || []).map((group) => ({
      label: group?.name,
      value: group?.id
    }))
  ]

  useEffect(() => {
    document.title = "Restaurant Management"
    dispatch(appActions.initFilters())
    setBreadCrumbs(createBreadcrumbs())
    dispatch(getCMSRestaurantGroupListAction({}))
  }, [])

  useEffect(() => {
    setLocalRestaurants(Array.isArray(cmsRestaurants) ? cmsRestaurants : cmsRestaurants?.data || [])
  }, [cmsRestaurants])

  useEffect(() => {
    setCurrentPage(1)
    setGroupCurrentPage(1)
  }, [debouncedSearch])

  useEffect(() => {
    if (activeTab === "restaurants") {
      dispatch(
        getCMSRestaurantListAction({
          search: debouncedSearch,
          owner: ownerFilter,
          lite: liteFilter
        })
      )
    } else {
      dispatch(getCMSRestaurantGroupListAction({ search: debouncedSearch }))
    }
  }, [activeTab, debouncedSearch, liteFilter, ownerFilter])

  const handleTabChange = (_, v) => {
    setActiveTab(v)
    setSearchValue("")
    setCurrentPage(1)
    setGroupCurrentPage(1)
    setLiteFilter(false)
    setOwnerFilter("")
  }

  const handleLiteChange = (item) => {
    setLiteFilter(item.value)
    setCurrentPage(1)
  }

  const handleOwnerChange = (item) => {
    setOwnerFilter(item.value)
    setCurrentPage(1)
  }

  const handleSearchField = (e) => {
    setSearchValue(e.target.value)
  }

  const enterReorderMode = () => {
    setReorderList([...localRestaurants])
    setIsReorderMode(true)
  }

  const exitReorderMode = (save) => {
    if (save) {
      setLocalRestaurants(reorderList)
      const active_restaurants = reorderList
        .filter((r) => !r.lite)
        .map((r, index) => ({ id: r.id, order: index + 1 }))
      const lite_restaurants = reorderList
        .filter((r) => r.lite)
        .map((r, index) => ({ id: r.id, order: index + 1 }))
      const payload = {
        ...(liteFilter ? { lite_restaurants } : { active_restaurants })
      }
      dispatch(
        reorderCMSRestaurantsAction({
          payload,
          onSuccess: () => {
            dispatch(
              getCMSRestaurantListAction({
                search: debouncedSearch,
                owner: ownerFilter,
                lite: liteFilter
              })
            )
          }
        })
      )
    }
    setIsReorderMode(false)
  }

  const handleCloseModal = () => {
    setModal(null)
    if (activeTab === "restaurants") {
      dispatch(
        getCMSRestaurantListAction({
          search: debouncedSearch,
          owner: ownerFilter,
          lite: liteFilter
        })
      )
    } else {
      dispatch(getCMSRestaurantGroupListAction({ search: debouncedSearch }))
    }
  }
  return (
    <React.Fragment>
      <Grid container>
        <Grid item xs={12}>
          <Header title="Restaurant Management" breadCrumbs={breadCrumbs}>
            {isReorderMode ? (
              <Box sx={{ display: "flex", gap: 1 }}>
                <AppButton
                  variant="outlined"
                  sx={{ padding: "10px 20px" }}
                  onClick={() => exitReorderMode(false)}>
                  Cancel
                </AppButton>
                <AppButtonGradient
                  sx={{ padding: "10px 20px" }}
                  onClick={() => exitReorderMode(true)}>
                  Save Order
                </AppButtonGradient>
              </Box>
            ) : (
              <Box sx={{ display: "flex", gap: 1 }}>
                {activeTab === "restaurants" && liteFilter !== "all" && !searchValue && (
                  <AppButton
                    sx={{ padding: "10px 20px" }}
                    startIcon={<DragIndicatorIcon />}
                    onClick={enterReorderMode}>
                    Reorder
                  </AppButton>
                )}
                <AppButton
                  onClick={() =>
                    setModal({
                      type: activeTab === "restaurants" ? "restaurant" : "group",
                      id: null
                    })
                  }
                  sx={{ padding: "10px 20px" }}
                  startIcon={<AddIcon />}>
                  {activeTab === "restaurants" ? "Add Restaurant" : "Add Group"}
                </AppButton>
              </Box>
            )}
          </Header>
        </Grid>
      </Grid>

      <TabContext value={activeTab}>
        <Box sx={{ borderBottom: `2px solid ${theme.palette.background.main}` }}>
          <CustomTabList onChange={handleTabChange}>
            <Tab
              icon={<RestaurantIcon fontSize="small" />}
              iconPosition="start"
              label="Restaurants"
              value="restaurants"
            />
            <Tab
              icon={<GroupsIcon fontSize="small" />}
              iconPosition="start"
              label="Restaurant Groups"
              value="groups"
            />
          </CustomTabList>
        </Box>
      </TabContext>

      <Grid container>
        {isReorderMode && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              Drag rows to reorder. Click <strong>Save Order</strong> to apply or{" "}
              <strong>Cancel</strong> to discard changes.
            </Alert>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          sx={{ mt: 3 }}
          className="d-flex align-items-end justify-content-end gap-2 mb-3 flex-wrap">
          {!isReorderMode && (
            <>
              <CustomSearchField
                type="search"
                autoComplete="off"
                value={searchValue}
                onChange={handleSearchField}
                placeholder="Search"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <img src={SearchIcon} style={{ cursor: "pointer", fontSize: "30px" }} />
                    </InputAdornment>
                  )
                }}
              />
              {activeTab === "restaurants" && (
                <>
                  <Box sx={{ width: 160 }}>
                    <AppSelect
                      id="lite-filter"
                      label="Lite Mode"
                      value={liteFilter}
                      menuItems={[
                        { label: "All", value: "all" },
                        { label: "Yes", value: "true" },
                        { label: "No", value: "false" }
                      ]}
                      onMenuClick={handleLiteChange}
                    />
                  </Box>
                  <Box sx={{ width: 220 }}>
                    <AppSelect
                      id="owner-filter"
                      label="Group"
                      value={ownerFilter}
                      menuItems={groupFilterOptions}
                      onMenuClick={handleOwnerChange}
                    />
                  </Box>
                  {(liteFilter || ownerFilter) && (
                    <AppButton
                      variant="outlined"
                      sx={{ padding: "11px 16px", alignSelf: "flex-end" }}
                      onClick={() => {
                        setLiteFilter(false)
                        setOwnerFilter("")
                        setCurrentPage(1)
                      }}>
                      Clear Filters
                    </AppButton>
                  )}
                </>
              )}
            </>
          )}
        </Grid>

        <Grid item xs={12}>
          {activeTab === "restaurants" ? (
            <RestaurantListTable
              rows={localRestaurants}
              isLoading={isLoading}
              currentPage={currentPage}
              setPage={setCurrentPage}
              onEdit={(r) => setModal({ type: "restaurant", id: r.id })}
              isReorderMode={isReorderMode}
              reorderList={reorderList}
              onReorderListChange={setReorderList}
            />
          ) : (
            <GroupListTable
              rows={cmsRestaurantGroups?.results || []}
              isLoading={isLoading}
              onEdit={(g) => setModal({ type: "group", id: g.id })}
              currentPage={groupCurrentPage}
              setPage={setGroupCurrentPage}
            />
          )}
        </Grid>
      </Grid>

      <RestaurantFormModal
        open={modal?.type === "restaurant"}
        onClose={handleCloseModal}
        restaurantId={modal?.id}
      />
      <RestaurantGroupFormModal
        open={modal?.type === "group"}
        onClose={handleCloseModal}
        groupId={modal?.id}
      />
    </React.Fragment>
  )
}

export default RestaurantManagement
